// src/ui/Sheet.tsx (T1): Direction D's sheet. On phone and tablet (up to 1023px) it is a bottom sheet (grab
// handle, 16px top corners, sticky footer; 640px wide and centred on tablet). On desktop it is a 440px right-hand
// side panel; `variant="modal"` is a centred dialog (the invite, the welcome player) and `variant="dock"` is a
// sticky column at 1024px and up that turns into a bottom sheet below.
//
// Behavior: a scrim closes it, Esc closes the top-most sheet, Tab stays inside while it is open, and focus goes
// to the sheet's [autofocus] element, else its heading, never a text field (so no phone keyboard pops up
// uninvited). Focus returns to where it was on close. On phone and tablet the grab handle and the head drag the
// sheet down: past 30% of its height, or on a quick flick, it closes. Root class: hb-sheet.
import type { ComponentChildren } from "preact";
import { useEffect, useId, useRef, useState } from "preact/hooks";
import { IconButton } from "./Button";
import { cx } from "./cx";
import { Eyebrow } from "./Type";

export type SheetVariant = "panel" | "modal" | "dock";

const open: string[] = [];           // ids of open sheets, top-most last
const OPEN_CLASS = "hb-sheet-open";
const DESKTOP = "(min-width: 1024px)";

function syncRootClass(): void {
  document.documentElement.classList.toggle(OPEN_CLASS, open.length > 0);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), video[controls]';

function isDesktop(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(DESKTOP).matches;
}

export function Sheet(props: {
  open: boolean;
  onClose: () => void;
  /** The heading (Title Case; idea titles stay sentence case). Always rendered, visually hidden with `hideTitle`. */
  title: ComponentChildren;
  eyebrow?: ComponentChildren;
  /** A small line above the title ("3 of 8 · Kitchen"). */
  meta?: ComponentChildren;
  /** A short line under the title ("Name · Role · 0:40"). */
  sub?: ComponentChildren;
  /** Extra controls in the head, before the close button (previous and next). */
  headActions?: ComponentChildren;
  footer?: ComponentChildren;
  variant?: SheetVariant;
  /** Desktop width of a modal in px (default 720). */
  width?: number;
  hideTitle?: boolean;
  /** No close button: the sheet's own buttons, the scrim, Esc and a drag close it (the install sheet). */
  noClose?: boolean;
  closeLabel?: string;
  class?: string;
  bodyClass?: string;
  children?: ComponentChildren;
}) {
  const variant = props.variant ?? "panel";
  const dock = variant === "dock";
  const [mounted, setMounted] = useState(props.open || dock);
  const [shown, setShown] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const sheetId = useId();
  const onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;
  const drag = useRef<{ id: number; y0: number; t0: number; dy: number } | null>(null);

  // Drag to dismiss (phone and tablet): only from the grab handle and the head, never the scrolling body.
  const onDragStart = (e: PointerEvent) => {
    if (isDesktop() || e.button !== 0 || !ref.current) return;
    if ((e.target as HTMLElement).closest("button, a, input, select, textarea, [role='button']")) return;
    drag.current = { id: e.pointerId, y0: e.clientY, t0: e.timeStamp, dy: 0 };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onDragMove = (e: PointerEvent) => {
    const d = drag.current;
    const el = ref.current;
    if (!d || e.pointerId !== d.id || !el) return;
    d.dy = Math.max(0, e.clientY - d.y0);
    el.style.transition = "none";
    el.style.transform = `translateY(${d.dy}px)`;
  };
  const onDragEnd = (e: PointerEvent) => {
    const d = drag.current;
    const el = ref.current;
    if (!d || e.pointerId !== d.id) return;
    drag.current = null;
    if (el) {
      el.style.transition = "";
      el.style.transform = "";
    }
    const speed = d.dy / Math.max(1, e.timeStamp - d.t0);
    if (e.type === "pointerup" && el && (d.dy > el.offsetHeight * 0.3 || (d.dy > 40 && speed > 0.6))) onCloseRef.current();
  };
  const dragHandlers = { onPointerDown: onDragStart, onPointerMove: onDragMove, onPointerUp: onDragEnd, onPointerCancel: onDragEnd };

  // Mount, then add .open on the next frame so the transform transition runs; remove, then unmount.
  useEffect(() => {
    if (props.open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    if (dock) return;
    const t = window.setTimeout(() => setMounted(false), 260);
    return () => window.clearTimeout(t);
  }, [props.open, dock]);

  // While open: register as top-most, move focus in, trap Tab, close on Esc; restore focus after.
  useEffect(() => {
    if (!props.open) return;
    if (dock && isDesktop()) return;
    returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    open.push(sheetId);
    syncRootClass();
    const focusTimer = window.setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const target =
        el.querySelector<HTMLElement>("[autofocus], [data-autofocus]") ??
        el.querySelector<HTMLElement>(".sheet-head h2, .sheet-head h3") ??
        el;
      if (!target.matches(FOCUSABLE)) target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    }, 60);
    const onKey = (e: KeyboardEvent) => {
      if (open[open.length - 1] !== sheetId) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !ref.current) return;
      const items = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((x) => x.offsetParent !== null || x === document.activeElement);
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !ref.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !ref.current.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKey);
      const i = open.indexOf(sheetId);
      if (i >= 0) open.splice(i, 1);
      syncRootClass();
      const back = returnTo.current;
      if (back && document.contains(back) && back.offsetParent !== null) back.focus({ preventScroll: true });
    };
  }, [props.open, dock, sheetId]);

  if (!mounted) return null;
  const isOpen = shown && props.open;
  const style = props.width ? { "--sheet-w": `${props.width}px` } : undefined;
  return (
    <>
      <div class={cx("hb-scrim", "scrim", isOpen && "open", dock && "scrim-dock")} onClick={() => props.onClose()} aria-hidden="true" />
      <section
        ref={ref}
        class={cx("hb-sheet", "sheet", variant === "modal" && "sheet-modal", dock && "sheet-dock", isOpen && "open", props.class)}
        role={dock && !props.open ? "complementary" : "dialog"}
        aria-modal={props.open ? "true" : undefined}
        aria-labelledby={titleId}
        style={style}
        data-variant={variant}
      >
        <div class="sheet-grab" aria-hidden="true" {...dragHandlers} />
        <header class="sheet-head" {...dragHandlers}>
          <div class="sheet-title">
            {props.meta ? <p class="t-meta">{props.meta}</p> : null}
            {props.eyebrow ? <Eyebrow>{props.eyebrow}</Eyebrow> : null}
            <h2 class={cx("h-sec", props.hideTitle && "sr")} id={titleId}>
              {props.title}
            </h2>
            {props.sub ? <p class="sheet-sub">{props.sub}</p> : null}
          </div>
          {props.headActions}
          {props.noClose ? null : <IconButton icon="x" label={props.closeLabel ?? "Close"} onClick={() => props.onClose()} />}
        </header>
        <div class={cx("sheet-body", props.bodyClass)}>{props.children}</div>
        {props.footer ? <footer class="sheet-foot">{props.footer}</footer> : null}
      </section>
    </>
  );
}

/** A centred dialog on desktop, a bottom sheet on phone: the same Sheet with `variant="modal"`. */
export function Dialog(props: Omit<Parameters<typeof Sheet>[0], "variant">) {
  return <Sheet {...props} variant="modal" />;
}
